import { useState } from 'react';
import { useFileUpload } from './useFileUpload';
import type { DocumentRequest } from './useVerificationCenterHook';

export interface UboFileData {
  externalId: string;
  formData: FormData;
  fileType: string;
}

interface IFIleManagement {
  documents: {
    title: string;
    texts: { positive: string[]; negative: string[] };
  };
  setActiveStep: (value: number) => void;
  reportActOnVerificationCenter: (action: string) => void;
  reportErrorDuringFileUpload: (
    errorCode: string,
    httpStatus: number,
    errorMessage: string
  ) => void;
  activeDocumentRequest: DocumentRequest;
}
export const useFileManagement = ({
  documents,
  setActiveStep,
  reportActOnVerificationCenter,
  reportErrorDuringFileUpload,
  activeDocumentRequest,
}: IFIleManagement) => {
  const [llcFiles, setLlcFiles] = useState<FormData[]>([]);
  const [uboFiles, setUboFiles] = useState<UboFileData[]>([]);
  const [fileValue, setFileValue] = useState<null | File>(null);
  const handleReset = () => {
    setLlcFiles([]);
    setUboFiles([]);
    setFileValue(null);
    setActiveStep(0);
  };

  const {
    data: tokens,
    error,
    mutate,
    isPending,
  } = useFileUpload({ llcFiles, uboFiles, handleReset, reportErrorDuringFileUpload });

  const handleFileUpload = (file: File | undefined, fileType: string) => {
    if (!file) return;
    setFileValue(file);
    const fd = new FormData();

    fd.append('file', file, documents.title);
    fd.append('fileType', fileType);

    if (activeDocumentRequest.type === 'NON_US_UBO') {
      // For UBO documents, store with external ID
      setUboFiles([
        ...uboFiles,
        {
          externalId: activeDocumentRequest.externalId!,
          formData: fd,
          fileType,
        },
      ]);
    } else {
      // For LLC documents
      setLlcFiles([...llcFiles, fd]);
    }
    reportActOnVerificationCenter(`${documents.title}_selected`);
  };

  const handleFileClear = () => {
    if (activeDocumentRequest.type === 'NON_US_UBO') {
      const clearedFiles = uboFiles.filter(
        (uboFile) => uboFile.externalId !== activeDocumentRequest.externalId
      );
      setUboFiles(clearedFiles);
    } else {
      const clearedFiles = llcFiles.filter((file) => {
        const blob = file.get('file') as File | null;
        return blob?.name !== documents.title;
      });
      setLlcFiles(clearedFiles);
    }
  };

  return {
    llcFiles,
    setLlcFiles,
    uboFiles,
    setUboFiles,
    fileValue,
    setFileValue,
    tokens,
    mutate,
    error,
    handleReset,
    handleFileUpload,
    handleFileClear,
    isLoading: isPending,
  };
};
